import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { BrowserRouter as Router, Switch, Route, Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Trans, withTranslation, useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  Grid,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material';
import { Pagination } from '@mui/material';

import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineItem,
  TimelineDot,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import ProfileMenu from '../../profile/ProfileMenu.js';
import { getAvailableWordsLog, resetWordsLog } from '../../../modules/wordslog.js';
import { formatDate, formatUtcTime, formatLargeNumber } from '../../../helper.js';


const styles = {
  root: {
    width: '100%',
    '& .MuiTimelineOppositeContent-root': {
      flex: '1 !important',
    },
  },
  // the column headers stay in view while the log scrolls. The main frame carries the page padding
  // (24px, 8px on phones) and sticky measures from its content edge, so the cells climb the padding
  // plus a pixel to sit flush under the app bar
  stickyHead: {
    '& th': { position: 'sticky', top: { xs: -9, sm: -25 }, zIndex: 2, bgcolor: 'background.paper' },
  },
  // overflow auto would make the container the cells' scroll frame; phones keep the horizontal scroll
  tableContainer: {
    overflow: { xs: 'auto', sm: 'visible' },
  },
  // the sticky header cells and the striped rows paint over the paper's corners; rounding the
  // four corner cells gives the table the paper's outline (overflow hidden would kill the sticky head)
  table: (theme) => ({
    minWidth: 650,
    // a little less than the default 16px: room for five chips per row in the contents column
    '& td, & th': { px: 1.5 },
    '& thead th:first-of-type': { borderTopLeftRadius: theme.shape.borderRadius },
    '& thead th:last-of-type': { borderTopRightRadius: theme.shape.borderRadius },
    '& tbody tr:last-of-type td:first-of-type': { borderBottomLeftRadius: theme.shape.borderRadius },
    '& tbody tr:last-of-type td:last-of-type': { borderBottomRightRadius: theme.shape.borderRadius },
    '& tbody tr:last-of-type td': { borderBottom: 0 },
  }),
  label: {
    width: 200,
    // war '#fff': sticky Spalte, im Dark Mode eine hell leuchtende Insel
    background: 'background.paper',
    borderRight: 'solid 1px',
    borderRightColor: 'divider',
    position: 'sticky',
    left: 0,
    zIndex: 1,
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  statusChip: {
    marginLeft: '16px',
    fontSize: '1rem',
  },
  transactionAdd: {
    color: 'success.main',
    fontWeight: 'bold',
    '&::before': {
      content: '"+"',
    },
  },
  transactionSpend: {
    color: 'error.main',
    fontWeight: 'bold',
  },
  transactionInitialWordsLeft: {
    color: 'text.secondary',
    '&::before': {
      content: '"+"',
    },
  },
  transactionWordsLeft: {
    borderTop: 'solid 1px grey',
    fontWeight: '500',
    '&::before': {
      content: '"+"',
    },
  },
  errorMessageTxt: {
    textAlign: 'center',
    padding: '5rem',
  },
  // capped so a full run wraps onto chip rows instead of widening the column
  sectionChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.5,
    maxWidth: 480,
  },
};

// dialog label key per section token; the products sub-fields arrive as "products.variants"
const SECTION_LABELS = {
  products: 'products',
  'products.variants': 'variants',
  'products.options': 'options',
  collections: 'collections',
  pages: 'pages',
  navigation: 'navigation',
  blogs: 'blogs',
  articles: 'articles',
};

// bulk rows note "OUTDATED:true,Themes: 178423366012,PRODUCTS,products.variants": the flag, theme ids
// behind the "Themes:" token, then the sections (upper case in older rows)
const parseBulkSections = (value) => {
  const result = { outdated: false, themeIds: [], sections: [] };
  let inThemes = false;
  (value || '').split(',').map((token) => token.trim()).filter(Boolean).forEach((token) => {
    if (token.startsWith('OUTDATED:')) {
      result.outdated = token === 'OUTDATED:true';
      return;
    }
    if (token.startsWith('Themes:')) {
      inThemes = true;
      token = token.slice('Themes:'.length).trim();
      if (!token) return;
    }
    if (inThemes && /^\d+$/.test(token)) {
      result.themeIds.push(token);
      return;
    }
    inThemes = false;
    result.sections.push(token.toLowerCase());
  });
  // the note lists the products sub-fields last; the chips show them right behind "products"
  const subFields = result.sections.filter((section) => section.startsWith('products.'));
  if (subFields.length > 0) {
    const rest = result.sections.filter((section) => !section.startsWith('products.'));
    const at = rest.indexOf('products');
    result.sections = at === -1 ? [...rest, ...subFields] : [...rest.slice(0, at + 1), ...subFields, ...rest.slice(at + 1)];
  }
  return result;
};

const BulkSections = ({ value }) => {
  const { t } = useTranslation();
  const themes = useSelector((state) => state.themes.themes);
  const { outdated, themeIds, sections } = parseBulkSections(value);
  const chip = (key, label, props) => <Chip key={key} size="small" variant="outlined" label={label} {...props} />;

  return (
    <Box sx={styles.sectionChips}>
      {themeIds.map((id) => {
        const theme = themes?.find((item) => String(item.shopify_id) === id);
        return chip(`theme-${id}`, t('wordslog.themeChip', { name: theme ? theme.name : id }));
      })}
      {sections.map((section) => chip(section, SECTION_LABELS[section]
        ? t(`bulkTranslationFeature.settingsDialog.${SECTION_LABELS[section]}`)
        : section))}
      {outdated && chip('outdated', t('wordslog.outdatedIncluded'), { color: 'warning' })}
    </Box>
  );
};



const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.MuiTableCell-head': {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    verticalAlign: 'top'
  },
  '&.MuiTableCell-body': {
    fontSize: '14px',
    verticalAlign: 'top'
  },
}));

// the stripe sits on the cells, not the row: a row background ignores the corner cells' radius
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&.MuiTableRow-root:nth-of-type(odd) > td': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export function WordsLogTimeline(state, props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const availableWordsLog = useSelector((state) => state.wordslog.available_words_log);
  const totalPages = useSelector((state) => state.wordslog.totalPages);
  const currentPage = useSelector((state) => state.wordslog.currentPage);
  const total = useSelector((state) => state.wordslog.total);
  const errorMessage = useSelector((state) => state.wordslog.errorMessage);

  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const fetchItems = useCallback(async () => {
    if (errorMessage || isLoading || !hasMore) return;

    setIsLoading(true);
    dispatch(getAvailableWordsLog((res) => {
      console.log(res);
      setIsLoading(false);

      if (res.currentPage === res.totalPages) {
        window.removeEventListener('scroll', handleScroll);
        setHasMore(false);
      }
    }));
  }, [isLoading, hasMore]);

  const handleScroll = useCallback(() => {
    if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight || isLoading || !hasMore) return;
    console.log('handleScroll');
    fetchItems();
  }, [fetchItems, isLoading]);

  // declared before the fetch effect so the first fetch asks for page 1 of a fresh list
  useEffect(() => {
    dispatch(resetWordsLog());
  }, [dispatch]);

  useEffect(() => {
    console.log('useEffect');
    fetchItems();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fetchItems, handleScroll]);

  return (
    <Box sx={styles.root}>

      {(!availableWordsLog && !isLoading) || (availableWordsLog.length === 0 && !isLoading && !hasMore) || errorMessage ? (
        <Typography sx={styles.errorMessageTxt}>
          {errorMessage ? errorMessage : t('wordslog.sorryNoLogsAvailable')}
        </Typography>
      ) : (
        <React.Fragment>
          <TableContainer component={Paper} sx={styles.tableContainer}>
            <Table sx={styles.table} aria-label="customized table">
              <TableHead sx={styles.stickyHead}>
                <TableRow>
                  <TableCell align="left">{t('wordslog.tableDate')}</TableCell>
                  <TableCell align="left">{t('wordslog.tableType')}</TableCell>
                  <TableCell align="left">{t('wordslog.tableService')}</TableCell>
                  <TableCell align="left">{t('wordslog.tableTarget')}</TableCell>
                  <TableCell align="left">{t('wordslog.tableSections')}</TableCell>
                  <TableCell align="right">{t('wordslog.tableWords')}</TableCell>
                  <TableCell align="right">{t('wordslog.tableWordsBalance')}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {availableWordsLog && availableWordsLog.map((row) => (
                  <StyledTableRow key={`word-log-entry-${row.id}`}>
                    {/* date and time in UTC, the short month keeps the date on one line */}
                    <StyledTableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                      {formatDate(row.actionDate, { month: 'short', timeZone: 'UTC' })}
                      <br />
                      <Typography variant="caption">{formatUtcTime(row.actionDate)} · #{row.id}</Typography>
                    </StyledTableCell>
                    <StyledTableCell align="left">{row.actionType}</StyledTableCell>
                    <StyledTableCell align="left">{row.translationService}</StyledTableCell>
                    <StyledTableCell align="left">
                      {row.targetLanguage}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {row.actionType === 'single' ? (
                        <a href={`${window.location.href.replace(window.location.pathname, '')}${row.targetSections}`} target="_blank" rel="noopener noreferrer">
                          {row.targetSections}
                          {/* the log entry opens in a new tab, unlike the app's other links */}
                          <OpenInNewOutlinedIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', ml: 0.5 }} />
                        </a>
                      ) : (
                        <BulkSections value={row.targetSections} />
                      )}
                    </StyledTableCell>
                    {/* the words a translation covered - the same as the deduction for charged runs,
                        a number of its own for AI runs, which deduct nothing; none or unknown is a dash */}
                    <StyledTableCell align="right">
                      {row.wordCount ? formatLargeNumber(row.wordCount) : '\u2013'}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <Box component="span" sx={styles.transactionInitialWordsLeft}>
                        {formatLargeNumber(row.newWordTotal + (row.wordTransaction * -1))}
                      </Box>
                      <br />
                      <Box component="span" sx={[
                        row.wordTransaction > 0 && styles.transactionAdd,
                        row.wordTransaction < 0 && styles.transactionSpend,
                      ]}>
                        {formatLargeNumber(row.wordTransaction)}
                      </Box>
                      <br />
                      <Box component="span" sx={styles.transactionWordsLeft}>
                        {formatLargeNumber(row.newWordTotal)}
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
            {isLoading && hasMore && (
              <div style={{ textAlign: 'center', padding: availableWordsLog.length === 0 ? '5rem' : '1rem' }}>
                <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
              </div>
            )}
          </TableContainer>
        </React.Fragment>
      )}
    </Box>
  );
}

const WordsLogPage = (props) => {
  const { t } = useTranslation();
  const availableWords = useSelector((state) => state.shop.shop.auto_translations.available_words);

  return (
    <React.Fragment>
      <Grid container spacing={3} alignItems="flex-start" className="page-content" sx={styles.root}>
        <Grid size={12} className="page-content">
          {/* the balance sits at the heading's right, where other pages keep their title bar actions */}
          <Typography variant="h4" component="h2">
            {t('wordslog.title')}
            <span style={{ float: 'right', fontSize: 16, marginTop: 10 }}>
              {t('wordslog.availableAutoTranslationWords')}:&nbsp;
              <strong>{formatLargeNumber(availableWords || 0)}</strong>
            </span>
          </Typography>
          <Divider className="divider-white" />
        </Grid>
        <Grid size={12}>
          <Paper sx={styles.paper}>
            <WordsLogTimeline />
          </Paper>
        </Grid>
        <Grid size={3}>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default (WordsLogPage);
